import { Link } from "@tanstack/react-router";
import { DocMarks, Spinner } from "../../components/ui/misc";
import { Panel } from "../../components/ui/Panel";
import { useItems, useSites, useTree } from "../../lib/queries";
import { plural, useClient } from "./client-ui";
import { ItemsTable } from "./ItemsTable";
import { groupByTopLevel } from "./overview-model";

const PER_GROUP = 5;
const NETWORK_ROWS = 8;
const MORE = "text-sm text-green-ink hover:underline";
const NOTE = "text-sm text-ink-3";

/** The client page's Overview (redesign spec §5.2): documents by top-level page, the sites, the network equipment and printers. */
export function ClientOverview({ clientId }: { clientId: string }) {
  const { client } = useClient(clientId);
  const { data: docs, isLoading: loadingDocs } = useTree(clientId);
  const { data: sites, isLoading: loadingSites } = useSites(clientId);
  const { data: network, isLoading: loadingNetwork } = useItems(clientId, { category: "network" });
  const groups = groupByTopLevel(docs ?? []);
  const params = { clientId };

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Panel
        aria-label="Documents"
        title="Documents"
        className="lg:col-span-2"
        action={
          <Link to="/clients/$clientId/documents" params={params} className={MORE}>
            All {client?.counts.documents ?? docs?.length ?? 0}
          </Link>
        }
      >
        {loadingDocs ? <Spinner label="Loading documents…" /> : null}
        {docs && docs.length === 0 ? <p className={NOTE}>No documents yet.</p> : null}
        <div className="grid gap-x-7 gap-y-5 sm:grid-cols-2">
          {groups.map((g) => (
            <div key={g.id} className="flex min-w-0 flex-col gap-2">
              <h3 className="text-sm font-semibold text-green-ink">{g.title}</h3>
              {g.docs.length === 0 ? <span className={NOTE}>Nothing here yet.</span> : null}
              {g.docs.slice(0, PER_GROUP).map((d) => (
                <Link
                  key={d.id}
                  to="/d/$ref"
                  params={{ ref: d.id }}
                  className="flex min-w-0 flex-col text-ink hover:text-green-ink"
                >
                  <span className="truncate">{d.title}</span>
                  <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <DocMarks code={d.code} kind={d.kind} />
                  </span>
                </Link>
              ))}
              {g.docs.length > PER_GROUP ? (
                <Link to="/clients/$clientId/documents" params={params} className={MORE}>
                  +{g.docs.length - PER_GROUP} more
                </Link>
              ) : null}
            </div>
          ))}
        </div>
      </Panel>

      <Panel aria-label="Sites" title="Sites">
        {loadingSites ? <Spinner label="Loading sites…" /> : null}
        {sites && sites.length === 0 ? <p className={NOTE}>No sites yet.</p> : null}
        {(sites ?? []).map((s) => (
          <Link
            key={s.id}
            to="/clients/$clientId/sites"
            params={params}
            className="flex flex-col gap-0.5 rounded-lg bg-desk px-3.5 py-3 text-ink hover:bg-green-wash"
          >
            <strong className="font-semibold">{s.name}</strong>
            {s.address ? <span className="text-sm text-ink-2">{s.address}</span> : null}
            <span className="text-sm text-ink-3">
              {plural(s.counts.device, "device", "devices")} ·{" "}
              {plural(s.counts.network, "network item", "network items")}
            </span>
          </Link>
        ))}
      </Panel>

      <Panel
        aria-label="Network and printers"
        title="Network and printers"
        className="lg:col-span-3"
        action={
          <Link to="/clients/$clientId/network" params={params} className={MORE}>
            All {client?.counts.network ?? network?.length ?? 0}
          </Link>
        }
      >
        {loadingNetwork ? <Spinner label="Loading network equipment and printers…" /> : null}
        {network && network.length === 0 ? <p className={NOTE}>No network equipment or printers yet.</p> : null}
        {network && network.length > 0 ? (
          <ItemsTable clientId={clientId} items={network.slice(0, NETWORK_ROWS)} label="Network and printers" />
        ) : null}
      </Panel>
    </div>
  );
}
